import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Music24Capabilities } from 'fym-common/src/license/music24Plans';
import { TypedMemo } from '../../common/types';
import {
  featureGroupMessages,
  featureRowsInGroup,
  Music24FeatureRow,
  MUSIC24_FEATURE_GROUPS,
} from '../../music24/config/music24PlanFeatureRows';
import { SMLUpgradeFeature } from '../../music24/config/music24PlanCapabilities';
import { Panel, PanelHead } from '../../music24/components/primitives/Panel';
import { SubscriptionRowUsage, SubscriptionUsageRowKey, useSubscriptionUsage } from './useSubscriptionUsage';
import { SubscriptionFeatureRow } from './SubscriptionFeatureRow';
/* eslint-disable css-modules/no-unused-class -- shared with SubscriptionFeatureRow.tsx; each file uses its own slice of it */
import styles from './SubscriptionFeaturesList.module.scss';
/* eslint-enable css-modules/no-unused-class */

const USAGE_ROW_KEYS: SubscriptionUsageRowKey[] = ['pages', 'artists', 'seats'];

/** `undefined` for every row except the three `useSubscriptionUsage` counts. */
function usageForRow(
  row: Music24FeatureRow,
  usageByRow: Partial<Record<SubscriptionUsageRowKey, SubscriptionRowUsage>>
): SubscriptionRowUsage | undefined {
  const key = USAGE_ROW_KEYS.find((candidate) => candidate === row.key);
  return key === undefined ? undefined : usageByRow[key];
}

interface SubscriptionFeaturesListProps {
  capabilities: Music24Capabilities;
}

/**
 * Every `MUSIC24_FEATURE_ROWS` row that can be "not included" on a plan, mapped to the feature its
 * lock chip should name — every row must be listed here or it silently falls back to a bare "—"
 * with no chip (the fix-round-1 bug: `listeningInsights` was missing). A row left out on purpose
 * never shows a chip because its value is always present in some form (a number, or
 * "Low-res preview, watermarked"), never the compare table's "—".
 */
const LOCK_FEATURE_BY_ROW_KEY: Partial<Record<string, SMLUpgradeFeature>> = {
  presaveServices: SMLUpgradeFeature.presave,
  fanExport: SMLUpgradeFeature.fanExport,
  customDomain: SMLUpgradeFeature.customDomain,
  geography: SMLUpgradeFeature.geography,
  csvExport: SMLUpgradeFeature.csvExport,
  serviceFilter: SMLUpgradeFeature.musicServiceFilter,
  listeningInsights: SMLUpgradeFeature.insights,
  api: SMLUpgradeFeature.apiAccess,
  releases: SMLUpgradeFeature.releases,
  oldSongs: SMLUpgradeFeature.oldSongs,
  kitLook: SMLUpgradeFeature.kitLook,
  kitZip: SMLUpgradeFeature.kitZip,
  fullVideo: SMLUpgradeFeature.fullVideo,
  // The AI clips go into the music video, so the chip names the plan of the full-song videos (Pro).
  aiClips: SMLUpgradeFeature.fullVideo,
  earnings: SMLUpgradeFeature.earnings,
  streamingSplit: SMLUpgradeFeature.streamingSplit,
  // Zero on a plan (Free's `aiInsightsPerMonth`) reads as "not included" too — design r1 shows
  // "AI insights" with the "Artist" lock chip for a Free owner.
  socialAccounts: SMLUpgradeFeature.socialAccounts,
  x: SMLUpgradeFeature.x,
  scheduledPosts: SMLUpgradeFeature.scheduledPosts,
  videoCredits: SMLUpgradeFeature.videoCredits,
  aiInsights: SMLUpgradeFeature.aiInsights,
  // Outreach: Free has no pitches and no mailboxes; autopilot is Label.
  outreachPitchesPerDay: SMLUpgradeFeature.outreachRate,
  outreachMailboxes: SMLUpgradeFeature.outreachMailboxes,
  outreachAutopilot: SMLUpgradeFeature.outreachAutopilot,
  outreachFollowUps: SMLUpgradeFeature.outreach,
};

/**
 * Design r1's "What your plan includes" panel: `MUSIC24_FEATURE_ROWS`, grouped Release / Promote
 * / Measure / Team, each row showing usage where a client endpoint counts it
 * (`useSubscriptionUsage`) and a lock chip where the plan doesn't include the row at all.
 */
const SubscriptionFeaturesListComponent: React.FC<SubscriptionFeaturesListProps> = ({ capabilities }) => {
  const intl = useIntl();
  const usageByRow = useSubscriptionUsage();
  const groups = useMemo(() => MUSIC24_FEATURE_GROUPS.map((group) => ({ group, rows: featureRowsInGroup(group) })), []);

  return (
    <Panel>
      <PanelHead
        title={intl.formatMessage({
          id: 'app.sml.settings.subscription.includes.title',
          defaultMessage: 'What your plan includes',
        })}
      />
      {groups.map(({ group, rows }) => (
        <React.Fragment key={group}>
          <h4 className={styles.groupHeading}>{intl.formatMessage(featureGroupMessages[group])}</h4>
          {rows.map((row) => (
            <SubscriptionFeatureRow
              key={row.key}
              row={row}
              capabilities={capabilities}
              usage={usageForRow(row, usageByRow)}
              lockFeature={LOCK_FEATURE_BY_ROW_KEY[row.key]}
            />
          ))}
        </React.Fragment>
      ))}
    </Panel>
  );
};

export const SubscriptionFeaturesList = TypedMemo(SubscriptionFeaturesListComponent);
